import { useState } from "react";
import { useNotify, useRefresh, useUpdate } from "ra-core";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

import { useConfigurationContext } from "../root/ConfigurationContext";

/**
 * Mejora #3 (Musco): cambia el estado del contacto sin entrar al detalle.
 *
 * Click en el círculo de color → menú con las 4 opciones (Frío / Tibio /
 * Caliente / En contrato). Al elegir, hace optimistic update via
 * dataProvider. Útil para clasificar contactos rápidamente desde la lista
 * o desde la card de empresa.
 *
 * El click NO propaga al Link padre (stopPropagation) para que no navegue
 * al contacto al cambiar el estado.
 */
export const QuickStatusButton = ({
  contactId,
  status,
  className,
}: {
  contactId: number | string;
  status: string;
  className?: string;
}) => {
  const { noteStatuses } = useConfigurationContext();
  const [open, setOpen] = useState(false);
  const [update, { isPending }] = useUpdate();
  const notify = useNotify();
  const refresh = useRefresh();

  if (!noteStatuses) return null;

  const currentStatusObject = noteStatuses.find((s: any) => s.value === status);
  if (!currentStatusObject) return null;

  const handleSelect = (newStatus: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (newStatus === status) {
      setOpen(false);
      return;
    }
    update(
      "contacts",
      {
        id: contactId,
        data: { status: newStatus },
        previousData: { status },
      },
      {
        onSuccess: () => {
          notify("Estado actualizado", { type: "success" });
          refresh();
          setOpen(false);
        },
        onError: () => {
          notify("No se pudo actualizar el estado", { type: "error" });
        },
      },
    );
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setOpen(true);
          }}
          disabled={isPending}
          className={cn(
            "group relative inline-flex items-center justify-center w-5 h-5 rounded-full hover:ring-2 hover:ring-primary/40 transition-all cursor-pointer disabled:opacity-50",
            className,
          )}
          title={`Estado: ${currentStatusObject.label} — click para cambiar`}
          aria-label={`Cambiar estado del contacto (actual: ${currentStatusObject.label})`}
        >
          <span
            className="inline-block w-2.5 h-2.5 rounded-full"
            style={{ backgroundColor: currentStatusObject.color }}
          />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
        {noteStatuses.map((s: any) => (
          <DropdownMenuItem
            key={s.value}
            onClick={(e) => handleSelect(s.value, e)}
            className={cn(
              "flex items-center gap-2",
              s.value === status && "bg-muted font-medium",
            )}
          >
            <span
              className="inline-block w-2.5 h-2.5 rounded-full"
              style={{ backgroundColor: s.color }}
            />
            {s.label}
            {s.value === status && (
              <span className="ml-auto text-xs text-muted-foreground">
                (actual)
              </span>
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
